import { isAncestorOf } from "./hierarchy";

interface EntityPermissionsInput {
  currentUser: { id: string; role: string } | null;
  ownerId: string;
  users: Array<{ id: string; manager_id?: string | null }>;
}

/**
 * Computes generic owner/edit permissions for any entity with an owner.
 * Entity-specific flags (canDelete, canCheckIn) stay in page components.
 *
 * `canRevertToDraft` is intentionally narrower than `canEdit`: supervisors in
 * the management chain can edit but cannot revert. Only the owner or an admin
 * may move an entity back to draft.
 */
export function getEntityPermissions({
  currentUser,
  ownerId,
  users,
}: EntityPermissionsInput): {
  isOwner: boolean;
  canEdit: boolean;
  canRevertToDraft: boolean;
} {
  if (!currentUser) {
    return { isOwner: false, canEdit: false, canRevertToDraft: false };
  }

  const isOwner = currentUser.id === ownerId;
  const isAdminUser = currentUser.role === "admin";
  const isSupervisorInChain =
    currentUser.role === "supervisor" &&
    isAncestorOf(users, currentUser.id, ownerId);
  const canEdit = isOwner || isAdminUser || isSupervisorInChain;
  const canRevertToDraft = isOwner || isAdminUser;

  return { isOwner, canEdit, canRevertToDraft };
}
